import { useState } from "react";
import { QrCode, Copy, RefreshCw, Smartphone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import CrudPage, { Pill } from "@/components/CrudPage";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const CODECS = ["opus", "g722", "ulaw", "alaw", "g729"].map((c) => ({ value: c, label: c }));

const fields = [
  { name: "extension", label: "Extension Number", required: true, placeholder: "1001" },
  { name: "display_name", label: "Display Name", required: true, placeholder: "Jane Doe" },
  { name: "department_id", label: "Department", type: "select", optionsEndpoint: "/departments" },
  { name: "transport", label: "SIP Transport", type: "select", default: "tls",
    options: [{ value: "tls", label: "TLS (secure)" }, { value: "udp", label: "UDP" }, { value: "tcp", label: "TCP" }] },
  { name: "status", label: "Status", type: "select", default: "active",
    options: [{ value: "active", label: "Active" }, { value: "disabled", label: "Disabled" }] },
  { name: "caller_id_name", label: "Caller ID Name" },
  { name: "caller_id_number", label: "Caller ID Number" },
  { name: "outbound_caller_id", label: "Outbound Caller ID" },
  { name: "voicemail_enabled", label: "Voicemail", type: "switch", default: true },
  { name: "voicemail_pin", label: "Voicemail PIN" },
  { name: "vm_to_email", label: "Voicemail → Email", type: "switch", default: false, help: "Email new voicemails to the address below (via Microsoft Graph)" },
  { name: "vm_email", label: "Voicemail Email Address", placeholder: "user@company.com" },
  { name: "push_enabled", label: "Mobile Push", type: "switch", default: true, help: "Wake mobile SIP app for incoming calls" },
  { name: "dnd", label: "Do Not Disturb", type: "switch", default: false },
  { name: "cf_always", label: "Forward Always → ext/number" },
  { name: "cf_busy", label: "Forward on Busy" },
  { name: "cf_noanswer", label: "Forward on No Answer" },
  { name: "max_contacts", label: "Max Devices", type: "number", default: 3 },
  { name: "codecs", label: "Codecs", type: "multiselect", options: CODECS, default: ["opus", "g722", "ulaw", "alaw"], full: true },
  { name: "regenerate_password", label: "Regenerate SIP password", type: "switch", editOnly: true, full: true,
    help: "Turn on to issue a fresh SIP secret on save (device must re-register)" },
];

const columns = [
  { name: "extension", label: "Ext", render: (it) => <span className="font-mono font-semibold text-[hsl(var(--gold))]">{it.extension}</span> },
  { name: "display_name", label: "Name" },
  { name: "department_id", label: "Department", render: (it, h) => h.labelForValue(h.fields.find((f) => f.name === "department_id"), it.department_id) },
  { name: "transport", label: "Transport", render: (it) => <Pill tone="blue">{it.transport.toUpperCase()}</Pill> },
  { name: "push_enabled", label: "Push", render: (it) => it.push_enabled ? <Pill tone="green">On</Pill> : <Pill tone="gray">Off</Pill> },
  { name: "status", label: "Status", render: (it) => it.status === "active" ? <Pill tone="green">Active</Pill> : <Pill tone="gray">Disabled</Pill> },
];

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border/60 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`text-sm truncate ${mono ? "font-mono" : ""}`}>{String(value)}</span>
        <button onClick={() => { navigator.clipboard.writeText(String(value)); toast.success(`${label} copied`); }}
          className="text-muted-foreground hover:text-[hsl(var(--gold))]"><Copy size={13} /></button>
      </div>
    </div>
  );
}

export default function Extensions() {
  const [prov, setProv] = useState(null);
  const [open, setOpen] = useState(false);

  const showProvisioning = async (ext) => {
    try {
      const { data } = await api.get(`/extensions/${ext.id}/provisioning`);
      setProv({ ...data, ext_number: ext.extension });
      setOpen(true);
    } catch { toast.error("Could not load provisioning"); }
  };

  return (
    <>
      <CrudPage
        title="Extensions"
        subtitle="Unlimited SIP endpoints. Each extension gets an auto-generated SIP secret your users register from any softphone or mobile SIP app."
        endpoint="/extensions"
        columns={columns}
        fields={fields}
        testid="ext"
        addLabel="New Extension"
        rowActions={[{ key: "prov", label: "SIP Provisioning", icon: QrCode, onClick: showProvisioning }]}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-card border-border" data-testid="provisioning-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Smartphone size={18} className="text-[hsl(var(--gold))]" /> SIP Provisioning · Ext {prov?.ext_number}
            </DialogTitle>
            <DialogDescription className="sr-only">SIP registration credentials for this extension.</DialogDescription>
          </DialogHeader>
          {prov && (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground mb-2">
                Enter these into your smartphone SIP app (e.g. Zoiper, Linphone, GS Wave, Bria). Push wakes the app on incoming calls.
              </p>
              <Row label="Display Name" value={prov.display_name} />
              <Row label="Username / Auth ID" value={prov.username} mono />
              <Row label="Password" value={prov.password} mono />
              <Row label="Domain / Realm" value={prov.domain} mono />
              <Row label="SIP Server / Proxy" value={prov.server} mono />
              <Row label="Port" value={prov.port} mono />
              <Row label="Transport" value={prov.transport} />
              <Row label="SIP URI" value={prov.sip_uri} mono />
              <Row label="Voicemail" value={prov.voicemail} />
              <div className="flex items-center justify-between pt-2">
                <span className="text-xs text-muted-foreground">Push</span>
                {prov.push_enabled ? <Pill tone="green">Enabled</Pill> : <Pill tone="gray">Disabled</Pill>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
